import { API_BASE_URL } from '../core/apiConfig';
import type { Foodstuff } from '../models/Foodstuff';
import type { Meal } from '../models/Meal';


export const BACKEND_BASE_URL = API_BASE_URL;
const LOCAL_MEAL_PLACEHOLDER_URL = '/mealPlaceholder.png';

export type MealNutritionSummary = {
    energyKcal: number;
    proteinG: number;
    carbohydrateG: number;
    fatG: number;
    fibreG: number;
};

export function resolveFoodstuffWeightInGrams(foodstuff: Foodstuff): number | undefined {
    const directWeight = Number(foodstuff.weight);
    if (Number.isFinite(directWeight) && directWeight > 0) {
        return directWeight;
    }

    const value = Number(foodstuff.value);
    if (!Number.isFinite(value) || value <= 0) {
        return undefined;
    }

    const conversionFactor = foodstuff.measurementUnits?.find((unit) => unit.selected)?.conversionFactor;
    if (Number.isFinite(conversionFactor) && (conversionFactor as number) > 0) {
        return value * (conversionFactor as number);
    }

    return undefined;
}

export function calculateNutritionFromFoodstuffs(foodstuffs?: Foodstuff[]): MealNutritionSummary {
    return (foodstuffs || []).reduce<MealNutritionSummary>((acc, foodstuff) => {
        const weight = resolveFoodstuffWeightInGrams(foodstuff) || 0;
        acc.energyKcal += (foodstuff.energyInKcal || 0) * weight;
        acc.proteinG += (foodstuff.proteinMassFraction || 0) * weight;
        acc.carbohydrateG += (foodstuff.carbohydrateMassFraction || 0) * weight;
        acc.fatG += (foodstuff.fatMassFraction || 0) * weight;
        acc.fibreG += (foodstuff.fibreMassFraction || 0) * weight;
        return acc;
    }, {
        energyKcal: 0,
        proteinG: 0,
        carbohydrateG: 0,
        fatG: 0,
        fibreG: 0,
    });
}

export function calculateNutritionFromMeal(meal?: Meal): MealNutritionSummary {
    return calculateNutritionFromFoodstuffs(meal?.foodstuffs);
}

export function resolveMealImageUrl(meal: Meal): string {
    const filename = meal.images?.[0]?.filename;

    if (!filename) {
        return LOCAL_MEAL_PLACEHOLDER_URL;
    }

    if (filename === LOCAL_MEAL_PLACEHOLDER_URL) {
        return LOCAL_MEAL_PLACEHOLDER_URL;
    }

    if (meal.id && !/^https?:\/\//i.test(filename)) {
        return `${BACKEND_BASE_URL}/meals/id/${meal.id}/image/id/${filename}`;
    }

    return filename;
}

export function isLocalMealPlaceholder(url?: string): boolean {
    return url === LOCAL_MEAL_PLACEHOLDER_URL;
}

export function formatGrams(weight?: number, digits: number = 0): string {
    if (!Number.isFinite(weight)) {
        return '0 g';
    }

    return `${Number(weight).toFixed(digits)} g`;
}
